/**
 * Vista Tabela — table-layout fixed + ellipsis (zero scroll horizontal),
 * com colunas redimensionáveis por arrasto (padrão ResizableColumns do
 * painel, larguras persistidas por usuário no backend).
 * Em telas < 768px vira cards empilhados.
 */
import React, { useMemo } from "react";

import {
  ColumnResizeProvider,
  ResetColumnsButton,
  ResizableColgroup,
  ResizableTh,
} from "../../../components/ResizableColumns";
import { useAuth } from "../../../context/AuthContext";
import {
  SITUACAO_LABELS,
  SITUACAO_TONES,
  podeAcaoTipo,
} from "../../../services/solicitacoes";
import { colunaDoCard } from "./Kanban";
import PrazoChip from "./PrazoChip";
import { TableWrapper } from "../../../styles/shared";
import {
  TIPO_LABELS,
  chaveSolicitacao,
  contagemItens,
  dataCurta,
  elegivelParaLote,
  SELECAO_SO_LOTE,
} from "./helpers";
import {
  Checkbox,
  DesktopOnly,
  GhostBtn,
  LoteTag,
  LotesWrap,
  MobileOnly,
  Pill,
  SolCard,
  SolCardHead,
  SolCardMeta,
  Table,
  Td,
  Th,
  Tr,
} from "./styles";

const BOTAO_MENOR = { height: 26, padding: "0 10px", fontSize: 12 };

// Larguras iniciais em px (usuária ajusta arrastando a borda do cabeçalho;
// duplo clique na alça restaura a coluna).
const COLUNAS_TABELA = [
  { key: "numero", defaultWidth: 128 },
  { key: "tipo", defaultWidth: 64 },
  { key: "cliente", defaultWidth: 210 },
  { key: "pedido", defaultWidth: 84 },
  { key: "pecas", defaultWidth: 120 },
  { key: "solicitante", defaultWidth: 150 },
  { key: "data", defaultWidth: 86 },
  { key: "situacao", defaultWidth: 196 },
  { key: "acoes", defaultWidth: 104 },
];
const COLUNA_SELECAO = { key: "sel", defaultWidth: 40 };

function CelulaSelecao({ s, selecionadas, onToggleSelecao, selecao }) {
  return (
    <Checkbox
      checked={selecionadas.has(chaveSolicitacao(s))}
      disabled={!selecao.pode(s)}
      title={selecao.titulo(s)}
      onChange={() => onToggleSelecao(s)}
      onClick={(e) => e.stopPropagation()}
      aria-label={`Selecionar ${s.numero}`}
    />
  );
}

export default function Tabela({
  itens,
  colunas: colunasKanban,
  selecionadas,
  mostrarSelecao,
  onToggleSelecao,
  onToggleTodas,
  onAbrir,
  onAutorizar,
  selecao = SELECAO_SO_LOTE,
}) {
  const { hasPermission } = useAuth();

  // A pílula é SEMPRE a situação canônica — é por ela que o filtro
  // "Situação" e os KPIs contam. Até 18/09/2026 a pílula mostrava o título da
  // coluna do quadro, e a tela filtrava "Aguardando autorização" exibindo
  // "Aguardando Entrega". A etapa do quadro vira informação secundária.
  const situacaoTexto = (s) => SITUACAO_LABELS[s.situacao] || s.situacao;
  const etapaTexto = (s) => {
    // Só a etapa OPERACIONAL acrescenta informação; a coluna que ancora a
    // própria situação ("Autorizadas") repetiria a pílula.
    const coluna = colunaDoCard(s, colunasKanban);
    if (!coluna || (coluna.situacoes || []).includes(s.situacao)) return "";
    return coluna.titulo || "";
  };

  // Autorizar só aparece pra quem pode autorizar AQUELE tipo (RBAC granular).
  const podeAutorizarTipo = (s) => podeAcaoTipo(hasPermission, "autorizar", s.tipo);

  const colunas = useMemo(
    () => (mostrarSelecao ? [COLUNA_SELECAO, ...COLUNAS_TABELA] : COLUNAS_TABELA),
    [mostrarSelecao],
  );

  const elegiveis = itens.filter(selecao.pode);
  const todasMarcadas =
    elegiveis.length > 0 && elegiveis.every((s) => selecionadas.has(chaveSolicitacao(s)));

  // Abre o modal "Autorizar e classificar" (a transição pede os eixos da
  // não-qualidade por item — nunca autoriza direto daqui).
  const autorizar = (s) => (e) => {
    e.stopPropagation();
    onAutorizar?.(s);
  };

  return (
    <>
      <DesktopOnly>
        <ColumnResizeProvider
          tableId="gestao-solicitacoes"
          persistirNoBackend
          columns={colunas}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", margin: "-6px 0 0" }}>
            <ResetColumnsButton />
          </div>
          <TableWrapper>
            <Table>
              <ResizableColgroup />
            <thead>
              <tr>
                {mostrarSelecao && (
                  <ResizableTh as={Th} columnKey="sel" resizable={false} $sel>
                    <Checkbox
                      checked={todasMarcadas}
                      disabled={elegiveis.length === 0}
                      title={
                        elegiveis.length === 0
                          ? "Nenhuma solicitação selecionável nos filtros atuais"
                          : "Selecionar todas as selecionáveis filtradas"
                      }
                      onChange={onToggleTodas}
                      aria-label="Selecionar todas as solicitações selecionáveis"
                    />
                  </ResizableTh>
                )}
                <ResizableTh as={Th} columnKey="numero">Nº</ResizableTh>
                <ResizableTh as={Th} columnKey="tipo">Tipo</ResizableTh>
                <ResizableTh as={Th} columnKey="cliente">Cliente</ResizableTh>
                <ResizableTh as={Th} columnKey="pedido">Pedido</ResizableTh>
                <ResizableTh as={Th} columnKey="pecas">Peças/Itens</ResizableTh>
                <ResizableTh as={Th} columnKey="solicitante">Solicitante</ResizableTh>
                <ResizableTh as={Th} columnKey="data">Data</ResizableTh>
                <ResizableTh as={Th} columnKey="situacao">Situação</ResizableTh>
                <ResizableTh as={Th} columnKey="acoes">Ações</ResizableTh>
              </tr>
            </thead>
            <tbody>
              {itens.map((s) => (
                <Tr key={chaveSolicitacao(s)} onClick={() => onAbrir(s)}>
                  {mostrarSelecao && (
                    <Td $sel onClick={(e) => e.stopPropagation()}>
                      <CelulaSelecao
                        s={s}
                        selecionadas={selecionadas}
                        onToggleSelecao={onToggleSelecao}
                        selecao={selecao}
                      />
                    </Td>
                  )}
                  <Td className="mono" title={s.numero}>
                    {s.numero}
                  </Td>
                  <Td title={TIPO_LABELS[s.tipo] || s.tipo}>
                    <Pill $tone="neutral">{s.tipo}</Pill>
                  </Td>
                  <Td title={s.cliente_nome || ""}>{s.cliente_nome || "—"}</Td>
                  <Td className="mono" title={s.num_pedido || ""}>
                    {s.num_pedido || "—"}
                  </Td>
                  <Td title={contagemItens(s)}>
                    {contagemItens(s)}
                    {elegivelParaLote(s) && (
                      <span style={{ color: "var(--ink-2)", fontSize: 11 }}>
                        {" "}
                        · {s.pecas_elegiveis_count} MDF
                      </span>
                    )}
                  </Td>
                  <Td title={s.criado_por_nome || ""}>{s.criado_por_nome || "—"}</Td>
                  <Td className="mono" title={dataCurta(s.created_at)}>
                    {dataCurta(s.created_at)}
                  </Td>
                  <Td title={[situacaoTexto(s), etapaTexto(s)].filter(Boolean).join(" · ")}>
                    <Pill $tone={SITUACAO_TONES[s.situacao]}>
                      {situacaoTexto(s)}
                    </Pill>
                    {etapaTexto(s) && (
                      <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        No quadro: {etapaTexto(s)}
                      </div>
                    )}
                    <PrazoChip solicitacao={s} bloco />
                    {(s.lotes_numeros || []).length > 0 && (
                      <LotesWrap style={{ marginTop: 4 }}>
                        {s.lotes_numeros.map((num) => (
                          <LoteTag key={num} title={`Peças no lote ${num}`}>
                            LOTE {num}
                          </LoteTag>
                        ))}
                      </LotesWrap>
                    )}
                  </Td>
                  <Td onClick={(e) => e.stopPropagation()}>
                    {s.situacao === "ABERTA" && podeAutorizarTipo(s) && (
                      <GhostBtn
                        type="button"
                        style={BOTAO_MENOR}
                        title="Autorizar e classificar os itens"
                        onClick={autorizar(s)}
                      >
                        Autorizar
                      </GhostBtn>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
            </Table>
          </TableWrapper>
        </ColumnResizeProvider>
      </DesktopOnly>

      <MobileOnly>
        {itens.map((s) => (
          <SolCard key={chaveSolicitacao(s)} onClick={() => onAbrir(s)}>
            <SolCardHead>
              {mostrarSelecao && selecao.pode(s) && (
                <CelulaSelecao
                  s={s}
                  selecionadas={selecionadas}
                  onToggleSelecao={onToggleSelecao}
                  selecao={selecao}
                />
              )}
              <strong>{s.numero}</strong>
              <Pill $tone="neutral">{s.tipo}</Pill>
              <Pill $tone={SITUACAO_TONES[s.situacao]}>
                {situacaoTexto(s)}
              </Pill>
              {etapaTexto(s) && <Pill $tone="neutral">{etapaTexto(s)}</Pill>}
            </SolCardHead>
            <SolCardMeta>
              <span>{s.cliente_nome || "Sem cliente"}</span>
              <span>Pedido {s.num_pedido || "—"}</span>
              <span>{contagemItens(s)}</span>
              <span>{dataCurta(s.created_at)}</span>
            </SolCardMeta>
            {(s.lotes_numeros || []).length > 0 && (
              <LotesWrap>
                {s.lotes_numeros.map((num) => (
                  <LoteTag key={num}>LOTE {num}</LoteTag>
                ))}
              </LotesWrap>
            )}
            <PrazoChip solicitacao={s} />
            {s.situacao === "ABERTA" && podeAutorizarTipo(s) && (
              <div onClick={(e) => e.stopPropagation()}>
                <GhostBtn type="button" style={BOTAO_MENOR} onClick={autorizar(s)}>
                  Autorizar
                </GhostBtn>
              </div>
            )}
          </SolCard>
        ))}
      </MobileOnly>
    </>
  );
}
